import { useEffect, useRef } from 'react';
import { useLocation, useMatches } from 'react-router-dom';
import { useAuth } from '@shared/auth/AuthContext';
import { useActiveSuite } from '../useActiveSuite';
import { buildCrumbs } from '../breadcrumbs/buildCrumbs';
import { getCrumbsSnapshot, subscribeCrumbs } from '@shared/shell/crumbStore';
import { setActivityScope, visitPage } from '@shared/activity';

export function useTrackVisits(): void {
  const location = useLocation();
  const matches = useMatches();
  const activeSuite = useActiveSuite();
  const { user, tenant, isAuthenticated } = useAuth();
  const prevScopeRef = useRef<string>('');

  // 1. Maintain store scope across user/tenant changes
  useEffect(() => {
    if (!isAuthenticated || !user) return;
    const scope = `${tenant?.id || 'anon'}:${user?.id || 'anon'}`;
    if (scope !== prevScopeRef.current) {
      prevScopeRef.current = scope;
      setActivityScope(tenant?.id, user?.id);
    }
  }, [isAuthenticated, user, tenant]);

  // 2. Track page navigation visits
  useEffect(() => {
    if (!isAuthenticated) return;

    const overrides = getCrumbsSnapshot();
    const crumbs = buildCrumbs(matches, activeSuite, overrides);
    const title = crumbs.length > 0
      ? crumbs[crumbs.length - 1].label
      : activeSuite?.name || location.pathname;

    visitPage({
      path: location.pathname,
      title,
      suiteId: activeSuite?.id || 'crm',
      icon: activeSuite?.icon,
    });
  }, [location.pathname, matches, activeSuite, isAuthenticated]);

  // 3. Update title if dynamic crumb arrives later
  useEffect(() => {
    if (!isAuthenticated) return;

    return subscribeCrumbs(() => {
      const overrides = getCrumbsSnapshot();
      const crumbs = buildCrumbs(matches, activeSuite, overrides);
      if (crumbs.length > 0) {
        const title = crumbs[crumbs.length - 1].label;
        visitPage({
          path: location.pathname,
          title,
          suiteId: activeSuite?.id || 'crm',
          icon: activeSuite?.icon,
          updateTitleOnly: true,
        });
      }
    });
  }, [location.pathname, matches, activeSuite, isAuthenticated]);
}
